import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Signature } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import {
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  IntegerInput,
  KeyValue,
  PhotoCapture,
  ScanField,
  useToast,
} from '@/ui'
import { STORAGE_FORM_CODE, type SignBayDeclarationPayload } from '../commands'
import {
  bagsAt,
  declarationInfo,
  isCleanAnswers,
  placePath,
  siteOf,
  type StoreState,
} from '../projection'
import { checkDeclarationPlace, classifyScan, declarationMissing } from '../rules'
import { DeclarationBadge } from '../components/DeclarationBadge'
import { YesNo } from '../components/bits'
import { useRefusalText } from '../components/useRefusalText'
import { StoreScreen } from '../components/StoreScreen'
import { useDraftState, useNow } from '../useStore'

type Answers = Record<string, boolean | number | null>

interface DeclDraft {
  bay_id: string | null
  answers: Answers
  photo_id: string | null
}

/** F5.4 — Storage declaration per bay (STORAGE_NO_FUMIGANT), with the bays due for renewal. */
export function DeclarationPage() {
  return (
    <StoreScreen title="store.declaration">
      {(state) => <DeclarationFlow state={state} />}
    </StoreScreen>
  )
}

function DeclarationFlow({ state }: { state: StoreState }) {
  const { t, locale } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const navigate = useNavigate()
  const actor = useActor()
  const now = useNow()
  const refusal = useRefusalText()
  const [params] = useSearchParams()
  const [draft, update, loaded, clear] = useDraftState<DeclDraft>(
    `store.declaration:${state.siteId}`,
    'store.declaration',
    () => ({ bay_id: params.get('bay'), answers: {}, photo_id: null }),
    actor.user_id,
  )
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const bay = draft.bay_id ? (state.locations.get(draft.bay_id) ?? null) : null
  const form = state.form
  const missing = declarationMissing(form, draft.answers)
  const clean = isCleanAnswers(form, draft.answers)
  const validTo = now + state.cache.settings.declaration_renewal_days * 86_400_000
  const brandBagsHere = bay
    ? bagsAt(state, bay.id).filter(
        (b) => b.stream === 'MAIN' && state.lots.get(b.unit_id)?.stream === 'MAIN',
      )
    : []

  // Bays that need attention: no declaration, expired, old form, not clean, or due soon.
  const bays = [...state.locations.values()].filter(
    (l) =>
      l.level === 'BAY' &&
      l.active &&
      (l.role ?? 'STORAGE') === 'STORAGE' &&
      siteOf(state, l.id)?.id === state.siteId,
  )
  const due = bays
    .map((b) => ({ bay: b, info: declarationInfo(state, b.id, now) }))
    .filter((x) => x.info.status !== 'CLEAN' || x.info.renewSoon)

  const setBay = (code: string | null) => {
    setError(null)
    if (!code) return update((d) => ({ ...d, bay_id: null }))
    const k = classifyScan(state, code)
    if (k.kind !== 'LOCATION') return setError(refusal('TAG_WRONG_KIND'))
    const r = checkDeclarationPlace(k.location)
    if (r) return setError(refusal(r))
    update((d) => ({ ...d, bay_id: k.location.id }))
  }

  const sign = async () => {
    if (!bay || missing.length || !draft.photo_id) return
    setSaving(true)
    try {
      const answers: Answers = {}
      for (const q of form.questions) answers[q.key] = draft.answers[q.key] ?? null
      const payload: SignBayDeclarationPayload = {
        declaration: {
          id: newId(),
          subject_type: 'LOCATION',
          subject_id: bay.id,
          form_code: STORAGE_FORM_CODE,
          form_version: form.version,
          answers,
          photo_id: draft.photo_id,
          signed_at: new Date().toISOString(),
        },
      }
      await enqueueCommand({
        type: 'sign_bay_declaration',
        payload,
        actorId: actor.user_id,
        mediaIds: [draft.photo_id],
      })
      await clear()
      toast.success(t('store.declaration.signed', { bay: bay.code }))
      navigate(-1)
    } catch (e) {
      toast.error(t('store.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return null
  const current = bay ? declarationInfo(state, bay.id, now) : null

  return (
    <div className="flex flex-col gap-4">
      {!bay && due.length > 0 && (
        <Card>
          <CardHeader eyebrow={t('store.declaration.due', { count: due.length })} />
          <CardBody className="py-1">
            <ul>
              {due.map(({ bay: b, info }) => (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => update((d) => ({ ...d, bay_id: b.id }))}
                    className="flex min-h-touch w-full items-center justify-between gap-2 border-b border-rule/70 py-2 text-left last:border-b-0"
                  >
                    <span>
                      <span className="block font-mono text-[14px]">{b.code}</span>
                      <span className="block text-[12px] text-muted">{placePath(state, b.id)}</span>
                    </span>
                    <DeclarationBadge info={info} />
                  </button>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody className="flex flex-col gap-3">
          <Field
            label={<BilingualLabel k="store.declaration.bayLabel" />}
            error={error ?? undefined}
            required
          >
            <ScanField value={bay?.code ?? null} onChange={(c) => setBay(c)} />
          </Field>
          {bay && current && (
            <div className="flex flex-col gap-1">
              <span className="text-[14px]">{placePath(state, bay.id)}</span>
              <div className="flex items-center gap-2">
                <DeclarationBadge info={current} />
                {current.declaration && (
                  <span className="text-[12px] text-muted">
                    {t('store.declaration.validTo', {
                      date: fmt.date(current.declaration.valid_to),
                    })}
                  </span>
                )}
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {bay && (
        <>
          <Card>
            <CardBody className="flex flex-col gap-5">
              {form.questions.map((q) => {
                if (q.required_if && draft.answers[q.required_if] !== true) return null
                const label = <BilingualLabel hi={q.hi} en={q.en} layout="stacked" />
                const v = draft.answers[q.key]
                return (
                  <Field key={q.key} label={label} required>
                    {q.kind === 'yes_no' ? (
                      <YesNo
                        value={typeof v === 'boolean' ? v : null}
                        onChange={(nv) =>
                          update((d) => ({ ...d, answers: { ...d.answers, [q.key]: nv } }))
                        }
                      />
                    ) : (
                      <IntegerInput
                        size="touch"
                        value={typeof v === 'number' ? v : null}
                        maxDigits={5}
                        onChange={(n) =>
                          update((d) => ({ ...d, answers: { ...d.answers, [q.key]: n } }))
                        }
                      />
                    )}
                  </Field>
                )
              })}
              <Field label={<BilingualLabel k="store.declaration.photo" />} required>
                <PhotoCapture
                  value={draft.photo_id}
                  onChange={(id) => update((d) => ({ ...d, photo_id: id }))}
                />
              </Field>
              <KeyValue
                items={[
                  {
                    label: <BilingualLabel k="store.declaration.validUntil" />,
                    value: fmt.date(validTo),
                  },
                  {
                    label: t('store.declaration.form'),
                    value: `${form.form_code} v${form.version}`,
                    mono: true,
                  },
                ]}
              />
            </CardBody>
          </Card>
          {missing.length === 0 && !clean && (
            <Banner tone="error" title={t('store.declaration.notCleanTitle')}>
              {t('store.declaration.notCleanBody', { count: brandBagsHere.length })}
            </Banner>
          )}
          <Button
            size="xl"
            variant="accent"
            fullWidth
            leadingIcon={<Signature aria-hidden="true" />}
            disabled={missing.length > 0 || !draft.photo_id}
            loading={saving}
            onClick={() => void sign()}
          >
            <BilingualLabel k="store.declaration.sign" />
          </Button>
          <p className="text-center text-[12px] text-muted" lang={locale}>
            {t('store.declaration.paperHint')}
          </p>
        </>
      )}
    </div>
  )
}
